import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Select, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button } from '../../../../components/ui';
import { createTenant, getTenant, tenantKeys } from '../../../../api/tenant.api';
import { toast, handleMutationError } from '../../../../lib/notification';
import type { CreateTenantPayload } from '../../../../types/iam-tenant.types';

type FormMode = 'create' | 'view';

function useTenantFormMode(): { mode: FormMode; tenantId: string | null } {
	const { pathname } = useLocation();

	return useMemo(() => {
		if (pathname.endsWith('/create')) {
			return { mode: 'create' as const, tenantId: null };
		}

		const segments = pathname.split('/');
		return { mode: 'view' as const, tenantId: segments[segments.length - 1] ?? null };
	}, [pathname]);
}

export default function TenantFormView() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mode, tenantId } = useTenantFormMode();
	const [form] = Form.useForm();
	const isReadOnly = mode === 'view';

	const { data: tenantDetail, isLoading: isTenantLoading } = useQuery({
		queryKey: tenantKeys.detail(tenantId!),
		queryFn: () => getTenant(tenantId!),
		enabled: !!tenantId,
	});

	// Populate form when tenant data loads
	if (tenantDetail && mode === 'view') {
		form.setFieldsValue({
			tenantCode: tenantDetail.tenantCode,
			tenantName: tenantDetail.tenantName,
			subDomain: tenantDetail.subDomain,
			themeMode: tenantDetail.themeMode,
			logo: tenantDetail.logo,
		});
	}

	const createMutation = useMutation({
		mutationFn: createTenant,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
			toast.success('Tenant created successfully');
			navigate('/iam/tenants');
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to create tenant');
		},
	});

	const handleSubmit = (values: CreateTenantPayload) => {
		const payload: CreateTenantPayload = {
			tenantCode: values.tenantCode,
			tenantName: values.tenantName,
			subDomain: values.subDomain,
			themeMode: values.themeMode || 'light',
			tokens: {},
			logo: values.logo || '',
		};
		createMutation.mutate(payload);
	};

	const pageTitle = mode === 'create' ? 'Create Tenant' : 'Tenant Details';

	if (tenantId && isTenantLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<Button
					variant="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate('/iam/tenants')}
					size="small"
				>
					Back to Tenants
				</Button>
			</div>

			<h2 className="text-lg font-semibold text-gray-900 mb-6">{pageTitle}</h2>

			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<div className="grid grid-cols-2 gap-6 max-w-3xl">
					<Form.Item
						name="tenantCode"
						label="Tenant Code"
						rules={[
							{ required: true, message: 'Please enter a tenant code' },
							{ min: 2, message: 'Tenant code must be at least 2 characters' },
							{ max: 50, message: 'Tenant code must not exceed 50 characters' },
							{ pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/, message: 'Tenant code must be alphanumeric with hyphens only (cannot start or end with a hyphen)' },
						]}
					>
						<Input placeholder="e.g. acme" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="tenantName"
						label="Tenant Name"
						rules={[
							{ required: true, message: 'Please enter a tenant name' },
							{ max: 200, message: 'Tenant name must not exceed 200 characters' },
						]}
					>
						<Input placeholder="e.g. Acme Corp" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="subDomain"
						label="Sub Domain"
						rules={[
							{ required: true, message: 'Please enter a sub domain' },
							{ min: 2, message: 'Subdomain must be at least 2 characters' },
							{ max: 50, message: 'Subdomain must not exceed 50 characters' },
						]}
					>
						<Input placeholder="e.g. acme" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item name="themeMode" label="Theme Mode">
						<Select
							placeholder="Select theme mode"
							size="large"
							disabled={isReadOnly}
							options={[
								{ label: 'Light', value: 'light' },
								{ label: 'Dark', value: 'dark' },
							]}
						/>
					</Form.Item>

					<Form.Item name="logo" label="Logo URL" className="col-span-2">
						<Input placeholder="https://cdn/logo.png" size="large" disabled={isReadOnly} />
					</Form.Item>
				</div>

				{!isReadOnly && (
					<div className="flex justify-end gap-3 pt-4">
						<Button variant="secondary" onClick={() => navigate('/iam/tenants')}>
							Cancel
						</Button>
						<Button variant="primary" htmlType="submit" loading={createMutation.isPending}>
							Create Tenant
						</Button>
					</div>
				)}
			</Form>
		</div>
	);
}
